import apiClient from '../core';
import type {
  User,
  CreateUserRequest,
  UpdateUserRequest,
  AdminUserResponse,
  AdminUsersListResponse,
  UserDetailsResponse,
} from '../../schemas/validation';

// Mimic response type
interface MimicResponse {
  success: boolean;
  token: string;
  mimickedUser: {
    id: string;
    name: string;
    email: string;
  };
  message: string;
}

export interface PlanCatalogUpdate {
  displayName: string;
  priceMonthly: number;
  maxSites: number;
  maxPagesPerSite: number;
  allowCustomDomain: boolean;
  allowContactForms: boolean;
  allowAdvancedComponents: boolean;
  allowCompeteStudio: boolean;
  features: string[];
}

// Admin API (Admin Only)
export const adminApi = {
  // User Management
  async getAllUsers(): Promise<User[]> {
    const response = await apiClient.get<AdminUsersListResponse>('/users');
    return response.data.data;
  },

  async getCurrentUser(): Promise<User> {
    const response = await apiClient.get<AdminUserResponse>('/users/me');
    return response.data.data;
  },

  async getUser(userId: string): Promise<User> {
    const response = await apiClient.get<AdminUserResponse>(`/users/${userId}`);
    return response.data.data;
  },

  async getUserDetails(userId: string): Promise<UserDetailsResponse> {
    const response = await apiClient.get<UserDetailsResponse>(`/users/${userId}/details`);
    return response.data;
  },

  async createUser(data: CreateUserRequest): Promise<User> {
    const response = await apiClient.post<AdminUserResponse>('/users', data);
    return response.data.data;
  },

  async updateUser(userId: string, data: UpdateUserRequest): Promise<User> {
    const response = await apiClient.put<AdminUserResponse>(`/users/${userId}`, data);
    return response.data.data;
  },

  async deleteUser(userId: string, password: string): Promise<void> {
    await apiClient.delete(`/users/${userId}`, { data: { password } });
  },

  async toggleAdminStatus(userId: string, password: string): Promise<User> {
    const response = await apiClient.put<AdminUserResponse>(`/users/${userId}/admin`, { password });
    return response.data.data;
  },

  // Site Management
  async getAllSites(): Promise<any[]> {
    const response = await apiClient.get('/admin/sites');
    return response.data;
  },

  async updateSite(
    siteId: string,
    data: { name: string; subdomain: string; customDomain: string | null }
  ): Promise<any> {
    const response = await apiClient.put(`/admin/sites/${siteId}`, data);
    return response.data;
  },

  async updateSiteCustomDomain(siteId: string, customDomain: string | null): Promise<any> {
    const response = await apiClient.put(`/admin/sites/${siteId}/custom-domain`, { customDomain });
    return response.data;
  },

  async deleteSite(siteId: string): Promise<void> {
    await apiClient.delete(`/admin/sites/${siteId}`);
  },

  async setLandingPage(
    siteId: string,
    isLandingPage: boolean
  ): Promise<{ success: boolean; data: any; message: string }> {
    const response = await apiClient.put<{ success: boolean; data: any; message: string }>(
      `/admin/sites/${siteId}/landing-page`,
      { isLandingPage }
    );
    return response.data;
  },

  // Mimic User functionality
  async startMimicSession(userId: string): Promise<MimicResponse> {
    const response = await apiClient.post<MimicResponse>(`/admin/users/${userId}/mimic`);
    return response.data;
  },

  async getUserBasicInfo(
    userId: string
  ): Promise<{ user: { id: string; name: string; email: string } }> {
    const response = await apiClient.get<{
      user: { id: string; name: string; email: string };
    }>(`/admin/users/${userId}/basic`);
    return response.data;
  },

  // User limit overrides and subscription info
  async getUserOverrides(userId: string): Promise<{
    success: boolean;
    data: {
      userId: string;
      name: string;
      email: string;
      overrideMaxSites: number | null;
      overrideMaxPagesPerSite: number | null;
      // Subscription info for price override UI
      hasActiveSubscription: boolean;
      subscriptionStatus: string | null;
      billingPeriod: string | null;
      planName: string | null;
      currentPlanPrice: number | null;
      hasCustomPrice: boolean;
    };
  }> {
    const response = await apiClient.get(`/admin/users/${userId}/overrides`);
    return response.data;
  },

  async updateUserOverrides(
    userId: string,
    overrides: {
      overrideMaxSites: number | null;
      overrideMaxPagesPerSite: number | null;
      overridePrice?: number | null; // Only for users with active subscriptions
    }
  ): Promise<{
    success: boolean;
    data: {
      userId: string;
      name: string;
      email: string;
      overrideMaxSites: number | null;
      overrideMaxPagesPerSite: number | null;
    };
    message: string;
    priceUpdateError?: string;
  }> {
    const response = await apiClient.put(`/admin/users/${userId}/overrides`, overrides);
    return response.data;
  },

  async updatePlan(planId: string, data: PlanCatalogUpdate): Promise<any> {
    const response = await apiClient.put(`/admin/plans/${planId}`, data);
    return response.data;
  },

  // Plan assignment (admin-assigned plans without Stripe payment)
  async assignUserPlan(
    userId: string,
    planName: string
  ): Promise<{
    success: boolean;
    message: string;
    data: {
      userId: string;
      userName: string;
      planId: string;
      planName: string;
      planDisplayName: string;
      isAdminAssigned: boolean;
    };
  }> {
    const response = await apiClient.post(`/admin/users/${userId}/assign-plan`, { planName });
    return response.data;
  },

  async removeUserPlan(userId: string): Promise<{
    success: boolean;
    message: string;
    data: {
      userId: string;
      userName: string;
    };
  }> {
    const response = await apiClient.delete(`/admin/users/${userId}/assign-plan`);
    return response.data;
  },

  // Marshmallow management
  async getUserMarshmallows(userId: string): Promise<{
    success: boolean;
    data: {
      userId: string;
      userName: string;
      email: string;
      remaining: number;
      total: number;
      resetDate: string;
    };
  }> {
    const response = await apiClient.get(`/admin/users/${userId}/marshmallows`);
    return response.data;
  },

  async addUserMarshmallows(
    userId: string,
    amount: number
  ): Promise<{
    success: boolean;
    data: {
      userId: string;
      userName: string;
      previousRemaining: number;
      added: number;
      newRemaining: number;
      newTotal: number;
    };
    message: string;
  }> {
    const response = await apiClient.post(`/admin/users/${userId}/marshmallows`, { amount });
    return response.data;
  },
};
